import { createSignal, onCleanup, type Accessor } from 'solid-js';
import type { ControlFeedback, ControlFeedbackStep } from '../lib/controlFeedback';

/**
 * The header's feedback slot: what it is saying, and how to make it say something else.
 *
 * One step at a time, latest wins, and a step that carries a lifetime takes the slot back down when it
 * expires. It lives here rather than inside the provider so a journey test can drive the REAL slot —
 * the same signal, the same replacement rule, the same clock — into the real header, instead of a
 * second copy of the rule written in the test.
 */
export interface ControlFeedbackSlot {
  feedback: Accessor<ControlFeedback | undefined>;
  show: (step: ControlFeedbackStep) => void;
  /**
   * Take the slot down without claiming anything.
   *
   * There is a real state in which the console does not know: the room has not reported a level commit
   * yet and the cloud relay has stopped reporting on it. Saying "Applied" there would be a guess and
   * saying "Not applied" would be a lie about a change the room has very probably already made, so the
   * slot says nothing instead — and the operator's level stays on screen, where the authored tree will
   * confirm or correct it.
   */
  clear: () => void;
}

export function useControlFeedback(): ControlFeedbackSlot {
  const [feedback, setFeedback] = createSignal<ControlFeedback>();
  let timer: number | undefined;

  function stopTimer(): void {
    if (timer != null) globalThis.clearTimeout(timer);
    timer = undefined;
  }

  function show(step: ControlFeedbackStep): void {
    stopTimer();
    setFeedback(step.feedback);
    if (step.clearAfter > 0) {
      timer = globalThis.setTimeout(() => setFeedback(undefined), step.clearAfter) as unknown as number;
    }
  }

  function clear(): void {
    stopTimer();
    setFeedback(undefined);
  }

  onCleanup(stopTimer);

  return { feedback, show, clear };
}
